import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import { ALL_PAGES_ITEM, PAGE_ITEMS_FOR_GRANT } from "../libs/pageGrantCatalog";
import type { GrantValue, GrantsByPageKey, PageGrantItem, PermissionGroupModalMode } from "../types/permissionGroups";

const GRANT_OPTIONS: GrantValue[] = ["권한 없음", "reader", "writer"];
const GRANT_OPTIONS_READER_ONLY: GrantValue[] = ["권한 없음", "reader"];

function GrantSelect({
    value,
    options,
    onChange,
}: {
    value: GrantValue;
    options: GrantValue[];
    onChange: (v: GrantValue) => void;
}) {
    return (
        <Select value={value} onValueChange={(v) => onChange(v as GrantValue)}>
            <SelectTrigger className="w-[160px]">
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {options.map((o) => (
                    <SelectItem key={o} value={o}>
                        {o}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

function GrantRow({
    item,
    grants,
    onSetGrant,
}: {
    item: PageGrantItem;
    grants: GrantsByPageKey;
    onSetGrant: (pageKey: string, value: GrantValue) => void;
}) {
    const options = item.maxGrant === "reader" ? GRANT_OPTIONS_READER_ONLY : GRANT_OPTIONS;
    const value = grants[item.key] ?? "권한 없음";

    return (
        <TableRow>
            <TableCell className="w-[40%]">{item.title}</TableCell>
            <TableCell className="w-[60%]">
                <GrantSelect value={value} options={options} onChange={(v) => onSetGrant(item.key, v)} />
            </TableCell>
        </TableRow>
    );
}

export function PermissionGroupDialog(props: {
    open: boolean;
    mode: PermissionGroupModalMode;
    groupName: string;
    onGroupNameChange: (v: string) => void;
    grants: GrantsByPageKey;
    onSetGrant: (pageKey: string, v: GrantValue) => void;
    errMsg: string;
    onCancel: () => void;
    onSubmit: () => void;
}) {
    return (
        <Dialog open={props.open} onOpenChange={(open) => !open && props.onCancel()}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle>{`사용자 권한 그룹 ${props.mode}`}</DialogTitle>
                </DialogHeader>

                <div className="flex-1 min-h-0 overflow-y-auto pr-2 space-y-4">
                    <div className="space-y-2">
                        <div className="text-sm font-medium">권한 그룹명</div>
                        <Input value={props.groupName} onChange={(e) => props.onGroupNameChange(e.target.value)} />
                    </div>

                    <Card className="p-3">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[40%]">페이지</TableHead>
                                    <TableHead className="w-[60%]">권한</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <GrantRow item={ALL_PAGES_ITEM} grants={props.grants} onSetGrant={props.onSetGrant} />
                            </TableBody>
                        </Table>
                    </Card>

                    <Card className="p-3">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[40%]">페이지</TableHead>
                                    <TableHead className="w-[60%]">권한</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {PAGE_ITEMS_FOR_GRANT.map((item) => (
                                    <GrantRow key={item.key} item={item} grants={props.grants} onSetGrant={props.onSetGrant} />
                                ))}
                            </TableBody>
                        </Table>
                    </Card>

                    {props.errMsg && (
                        <Card className="p-3 border-red-200 bg-red-50 text-red-900">
                            <div className="text-sm">{props.errMsg}</div>
                        </Card>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={props.onCancel}>
                        취소
                    </Button>
                    <Button onClick={props.onSubmit}>{props.mode}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}